import { defineAsyncComponent, type AsyncComponentLoader, type Component } from "vue";

/**
 * Dynamic icon loader composable
 * Dynamically imports Vue components from the svgs directory
 */
export const useDynamicIcon = () => {
    // Cache for loaded components to avoid re-importing
    const iconCache = new Map<string, Component>();

    /**
     * Dynamically loads an icon component from the svgs directory
     * @param iconName - Name of the icon (e.g., 'EyeIcon', 'HomeIcon')
     * @returns Promise resolving to the Vue component or null if not found
     */
    const loadIcon = (iconName: string): Component | null => {
        if (!iconName) return null;

        // Check cache first
        if (iconCache.has(iconName)) {
            return iconCache.get(iconName)!;
        }

        // Define async component with dynamic import
        const asyncComponent = defineAsyncComponent({
            loader: (() => {
                // Dynamic import from svgs directory
                return import(`@/shared/components/svgs/${iconName}.vue`);
            }) as AsyncComponentLoader,
            // Loading component (optional)
            loadingComponent: undefined,
            // Error component (optional)
            errorComponent: undefined,
            // Delay before showing loading component
            delay: 0,
            // Timeout for loading
            timeout: 3000,
            // Handle loading errors
            onError: (error: Error) => {
                console.warn(`Failed to load icon: ${iconName}`, error);
            },
        });

        // Cache the component
        iconCache.set(iconName, asyncComponent);
        return asyncComponent;
    };

    /**
     * Get available icon names from the svgs directory
     * This is a static list based on the current directory structure
     */
    const getAvailableIcons = (): string[] => {
        return [
            "CalendarIcon",
            "CancelIcon",
            "CheckedBoldIcon",
            "CheckedIcon",
            "CircleCheckIcon",
            "CircleCheckedIcon",
            "CircleFillCheckedIcon",
            "CirclePersonIcon",
            "Close",
            "ConcentrationIcon",
            "DatabaseIcon",
            "DownArrowIcon",
            "DragIcon",
            "ExitIcon",
            "EyeIcon",
            "GraphIcon",
            "HomeIcon",
            "InformationIcon",
            "LeftArrow",
            "LeftCarouselArrow",
            "LoadingCircle",
            "Logo",
            "MessageIcon",
            "MultiPeopleIcon",
            "OptionIcon",
            "PencilIcon",
            "PersonIcon",
            "QuestionIcon",
            "ReLoadIcon",
            "RedLineCheckIcon",
            "RightArrow",
            "RightCarouselArrow",
            "SeatIcon",
            "SettingIcon",
            "SmallArrowIcon",
            "SomeCheckBoxIcon",
            "ThreeBoxIcon",
            "TrophyIcon",
            "TwoPeopleIcon",
            "UnCheckedBoldIcon",
            "UnCheckedIcon",
        ];
    };

    /**
     * Check if an icon exists in the available icons list
     * @param iconName - Name of the icon to check
     * @returns boolean indicating if the icon exists
     */
    const isIconAvailable = (iconName: string): boolean => {
        return getAvailableIcons().includes(iconName);
    };

    return {
        loadIcon,
        getAvailableIcons,
        isIconAvailable,
    };
};
